import { useNavigate } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { type MouseEvent, type ReactNode, Suspense } from 'react';
import { cx } from '@/lib/cx';
import { formatRelative } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import type { InboxItem } from '@/lib/inbox';
import { EntityLink, Pill } from '@/ui';
import s from './Inbox.module.css';

/** A same-origin app link from a plain href (with its search), without a full page load. */
export function AppLink({ href, className, children, onNavigate }: { href: string; className?: string; children: ReactNode; onNavigate?: () => void }) {
  const navigate = useNavigate();
  const go = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    onNavigate?.();
    void navigate({ href });
  };
  return (
    <a href={href} className={className} onClick={go}>
      {children}
    </a>
  );
}

/**
 * Items of the attention inbox (lib/inbox): what it is, what it is about, when, and the buttons
 * that act on it right here. `compact` for the top bar's popover (one line of context).
 */
export function InboxList({ items, compact, onNavigate }: { items: InboxItem[]; compact?: boolean; onNavigate?: () => void }) {
  const now = useNow();
  return (
    <ul className={cx(s.list, compact && s.compact)}>
      {items.map((x) => {
        const overdue = !!x.due && new Date(x.due).getTime() < now;
        return (
          <li key={x.id} className={s.item}>
            <div className={s.head}>
              <Pill tone={x.tone}>{t(x.kind)}</Pill>
              {overdue && <Pill tone="bad">{t('Overdue')}</Pill>}
              <span className={s.when}>{x.due && !overdue ? t('due {when}', { when: formatRelative(x.due, now) }) : formatRelative(x.at, now)}</span>
            </div>
            {x.href ? (
              <AppLink href={x.href} className={s.title} onNavigate={onNavigate}>
                {x.title}
              </AppLink>
            ) : (
              <p className={s.title}>{x.title}</p>
            )}
            {x.detail && <p className={s.detail}>{x.detail}</p>}
            {(x.subject || (x.meta && x.meta.length > 0)) && (
              <p className={s.meta}>
                {x.subject && (
                  <EntityLink type={x.subject.type} id={x.subject.id}>
                    {x.subject.label ?? x.subject.type}
                  </EntityLink>
                )}
                {x.meta?.map((m) => (
                  <span key={m}>{m}</span>
                ))}
              </p>
            )}
            {(x.actions || x.href) && (
              <div className={s.actions}>
                <Suspense fallback={null}>{x.actions}</Suspense>
                {x.href && !compact && (
                  <AppLink href={x.href} className={s.open} onNavigate={onNavigate}>
                    {t('Details')}
                    <ArrowRight aria-hidden />
                  </AppLink>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
